import { V2Empty } from '../V2Empty.jsx';

// Defensive string coercion. Server-side payloads (summary.title, payload.label,
// payload.rationale, etc.) are typed as strings by the BFF contract but in
// practice can drift to objects / null when an upstream emitter changes shape.
// React error #31 ("Objects are not valid as a React child") then crashes the
// entire chat tree. Coerce at every render site so a single bad upstream row
// renders empty instead of taking down the panel.
function asText(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  // Objects / arrays / functions → empty. Logging is intentionally absent;
  // a misshaped row is recoverable and not actionable from the consumer.
  return '';
}

export function V2Summary({ summary, entities = [], commitments = [], decisions = [], citations = [], archivistEvs = [], events = [] }) {
  if (!summary && !events.length) return <V2Empty message="Send a message to generate a rolling summary." />;

  const threadStatus = 'IN PROGRESS'; // Slice 1 hardcode; Slice 2 reads thread state
  const turnCount = events.filter(e => (e?.event_type || e?.type) === 'message.created').length;

  // Derive open commitments (no due date resolved) vs decided decisions
  const open = commitments.filter(c => !(c?.payload?.status === 'resolved' || c?.status === 'resolved'));
  const decided = decisions.filter(d => d?.payload?.decided || d?.payload?.rationale);

  // Reconciled: decisions with dispute/supersede kind
  const reconciled = decisions.filter(d => {
    const k = d?.payload?.kind || d?.kind || '';
    return k === 'dispute' || k === 'supersede';
  });

  // Mentioned entities: all entities from event stream
  const mentioned = entities.slice(0, 6); // cap at 6 in Slice 1

  // Archivist quote: first archivist event with body text
  const archivistQuote = archivistEvs.find(ev => {
    const b = ev?.body || ev?.payload || {};
    return b.content || b.text || b.summary;
  });
  const quoteText = archivistQuote
    ? (asText((archivistQuote.body || archivistQuote.payload || {}).content)
       || asText((archivistQuote.body || archivistQuote.payload || {}).text)
       || asText((archivistQuote.body || archivistQuote.payload || {}).summary))
    : '';

  return (
    <div className="v2-summary" data-testid="v2-summary">
      {/* kicker + title */}
      <div className="v2-summary-kicker">Thread · {threadStatus}</div>
      {asText(summary?.title) && <div className="v2-summary-title">{asText(summary?.title)}</div>}
      {asText(summary?.body) && <div className="v2-summary-body">{asText(summary?.body)}</div>}

      {/* entity chips */}
      {mentioned.length > 0 && (
        <div className="v2-summary-entities">
          {mentioned.map((e, i) => {
            const label = asText(e?.label) || asText(e?.urn?.split(':').pop()) || '';
            return label ? <span key={i} className="v2-summary-entity-chip">• {label}</span> : null;
          })}
        </div>
      )}

      {/* rule + archivist quote */}
      {quoteText && (
        <>
          <hr className="v2-summary-rule" />
          <div className="v2-summary-quote">
            <span className="v2-summary-pilcrow">¶</span>
            {quoteText}
          </div>
        </>
      )}

      {/* OPEN / DECIDED columns */}
      {/* JOBE-0169: read display.label (server-projected) first; PR #1078 asText
          guards retained as defence in depth. Final fallback is "Item N" /
          "Decision N" for the edge case where the server omits the sidecar
          (unknown kind, malformed body). */}
      {(open.length > 0 || decided.length > 0) && (
        <>
          <hr className="v2-summary-rule" />
          <div className="v2-summary-od-row">
            <div className="v2-summary-od-col">
              <div className="v2-summary-od-head">Open {open.length}</div>
              {open.map((c, i) => {
                const label = asText(c?.display?.label) || `Item ${i+1}`;
                return <div key={i} className="v2-summary-open-item">□ {label}</div>;
              })}
            </div>
            <div className="v2-summary-od-col">
              <div className="v2-summary-od-head">Decided {decided.length}</div>
              {decided.map((d, i) => {
                const label = asText(d?.display?.label) || `Decision ${i+1}`;
                return <div key={i} className="v2-summary-decided-item">◆ {label}</div>;
              })}
            </div>
          </div>
        </>
      )}

      {/* RECONCILED */}
      {/* JOBE-0169: read display.entity_type / display.label first. The
          payload.kind / payload.rationale fishing the original code did
          isn't on the canonical kinds/decision.go schema; the underlying
          dispute/supersede classification depends on yet-to-wire upstream
          plumbing (see spec Followup). Until then, Reconciled renders
          empty against real data — honest, not stub. */}
      {reconciled.length > 0 && (
        <div className="v2-summary-reconciled">
          <div className="v2-summary-section-head">Reconciled {reconciled.length}</div>
          {reconciled.map((d, i) => {
            const entityType = asText(d?.display?.entity_type) || '';
            const text = asText(d?.display?.label) || '';
            return (
              <div key={i} className="v2-summary-reconciled-row">
                <span className={`v2-summary-reconcile-badge v2-badge-${entityType}`}>{entityType}</span>
                <span className="v2-summary-reconcile-text">{text}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* MENTIONED */}
      {mentioned.length > 0 && (
        <div className="v2-summary-mentioned">
          <div className="v2-summary-section-head">Mentioned {mentioned.length}</div>
          <div className="v2-summary-mentioned-chips">
            {mentioned.map((e, i) => {
              const label = e?.label || e?.urn?.split(':').pop() || '';
              return label ? <span key={i} className="v2-summary-entity-chip">• {label}</span> : null;
            })}
          </div>
        </div>
      )}

      {/* footer stats */}
      <div className="v2-summary-footer">
        {turnCount > 0 && `${turnCount} turns`}
        {citations.length > 0 && ` · ${citations.length} citations`}
        {archivistEvs.length > 0 && ` · archivist: ${archivistEvs.length} actions`}
      </div>
    </div>
  );
}
